<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useApiClient } from "@/api/client";
import { FilterPatch } from "@/ui/components/FileBrowser/types";
import TagChip from "@/ui/components/FileBrowser/TagChip.vue";
import PublicUserDO = App.Data.PublicUserDO;
import FileTagDTO = App.Data.FileTagDTO;

const props = defineProps<{
	kind: "date" | "user" | "tag";
	from: string | null;
	to: string | null;
	userId: number | null;
	tagIds: number[];
}>();

const emit = defineEmits<{ change: [patch: FilterPatch] }>();

const { t } = useI18n();

const DATE_PRESETS: {
	labelKey: string;
	from: string | null;
	to: string | null;
}[] = [
	{ labelKey: "anyTime", from: null, to: null },
	{ labelKey: "today", from: "now/d", to: "now" },
	{ labelKey: "yesterday", from: "now-1d/d", to: "now-1d/d" },
	{ labelKey: "last7Days", from: "now-7d/d", to: "now" },
	{ labelKey: "last30Days", from: "now-30d/d", to: "now" },
	{ labelKey: "thisMonth", from: "now/M", to: "now" },
	{ labelKey: "thisYear", from: "now/y", to: "now" },
];

const isActive = computed(() => {
	if (props.kind === "date") {
		return !!props.from || !!props.to;
	}

	return props.kind === "tag" ? props.tagIds.length > 0 : !!props.userId;
});

const activePreset = computed(
	() =>
		DATE_PRESETS.find(
			(preset) => preset.from === props.from && preset.to === props.to
		) ?? null
);

const users = ref<PublicUserDO[]>([]);
const isLoadingUsers = ref(false);
const hasLoadedUsers = ref(false);

const loadUsers = async (): Promise<void> => {
	if (hasLoadedUsers.value || isLoadingUsers.value) {
		return;
	}

	isLoadingUsers.value = true;

	const response = await useApiClient("users").getEnvironmentUsers();

	if (response.isOk()) {
		users.value = response.value;
	}

	hasLoadedUsers.value = true;
	isLoadingUsers.value = false;
};

const tags = ref<FileTagDTO[]>([]);
const isLoadingTags = ref(false);
const hasLoadedTags = ref(false);

const loadTags = async (): Promise<void> => {
	if (hasLoadedTags.value || isLoadingTags.value) {
		return;
	}

	isLoadingTags.value = true;

	const response = await useApiClient("fileTags").getFileTags();

	if (response.isOk()) {
		tags.value = response.value;
	}

	hasLoadedTags.value = true;
	isLoadingTags.value = false;
};

const isOpen = ref(false);

watch(isOpen, (open) => {
	if (!open) {
		return;
	}

	if (props.kind === "user") {
		loadUsers();
	}

	if (props.kind === "tag") {
		loadTags();
	}
});
</script>

<template>
	<v-menu v-model="isOpen" :close-on-content-click="kind === 'date'">
		<template #activator="{ props: menuProps }">
			<span
				v-bind="menuProps"
				class="caret"
				:class="{ 'caret--active': isActive }"
				@click.stop
			>
				<v-icon size="9">fa: fa-solid fa-caret-down</v-icon>
			</span>
		</template>

		<v-list v-if="kind === 'date'" density="compact" min-width="160">
			<v-list-item
				v-for="preset in DATE_PRESETS"
				:key="preset.labelKey"
				:active="activePreset?.labelKey === preset.labelKey"
				:title="t(`fileBrowser.dateFilters.${preset.labelKey}`)"
				@click="emit('change', { from: preset.from, to: preset.to })"
			/>
		</v-list>

		<v-card v-else-if="kind === 'tag'" min-width="240" class="pa-2">
			<v-autocomplete
				:model-value="tagIds"
				:items="tags"
				:loading="isLoadingTags"
				item-title="name"
				item-value="id"
				:label="t('fileBrowser.filterBy.tags')"
				density="compact"
				hide-details
				clearable
				chips
				closable-chips
				multiple
				autofocus
				:list-props="{ density: 'compact' }"
				:menu-props="{ class: 'tag-filter-menu' }"
				@update:model-value="(value) => emit('change', { tagIds: value ?? [] })"
			>
				<template #item="{ props: itemProps, item }">
					<v-list-item v-bind="itemProps">
						<template #title>
							<tag-chip
								:name="item.raw.name"
								:rgb-color-value="item.raw.rgb_color_value"
							/>
						</template>
					</v-list-item>
				</template>

				<template #chip="{ props: chipProps, item }">
					<tag-chip
						v-bind="chipProps"
						:name="item.raw.name"
						:rgb-color-value="item.raw.rgb_color_value"
					/>
				</template>
			</v-autocomplete>
		</v-card>

		<v-card v-else min-width="240" class="pa-2">
			<v-autocomplete
				:model-value="userId"
				:items="users"
				:loading="isLoadingUsers"
				item-title="name"
				item-value="id"
				:label="t('fileBrowser.filterBy.modifiedBy')"
				density="compact"
				hide-details
				clearable
				autofocus
				@update:model-value="
					(value) => emit('change', { userId: value ?? null })
				"
			/>
		</v-card>
	</v-menu>
</template>

<style lang="scss">
.tag-filter-menu.v-overlay .v-list .v-list-item {
	padding-inline: 8px;
}
</style>

<style scoped lang="scss">
.caret {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 14px;
	height: 14px;
	margin-left: 2px;
	border-radius: 3px;
	opacity: 0.35;
	cursor: pointer;

	&:hover {
		opacity: 0.9;
		background-color: rgba(var(--color-secondary), 0.9);
	}

	&--active {
		opacity: 1;
		color: rgb(var(--color-primary));
		background-color: rgb(var(--color-secondary));
	}
}
</style>
